// frontend/src/components/Quality/MeasurementList.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { measurementAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import './MeasurementList.css';

const MeasurementList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const LIMIT = 25;

  useEffect(() => { load(); }, [search, page]);

  const load = async () => {
    try {
      setLoading(true);
      const params = { page, limit: LIMIT };
      if (search) params.productCode = search;
      const res = await measurementAPI.getHeaders(params);
      setHeaders(res.data.data);
      setTotalPages(res.data.pagination.totalPages);
      setTotalItems(res.data.pagination.total);
      setError(null);
    } catch (err) {
      setError('Failed to load measurement specs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const stageLabel = (stage) => {
    const map = { PP: 'PP Sample', TOP: 'Top of Production', FINAL: 'Final', AQL: 'AQL' };
    return map[stage] || stage || '—';
  };

  return (
    <div className="settings-page">
      <div className="mlist-header">
        <div>
          <h1 className="mlist-title">📐 {t('quality.measurements', 'Measurement Specs')}</h1>
          <p className="mlist-sub">{totalItems} {t('quality.specs_found', 'specifications found')}</p>
        </div>
      </div>

      {/* Search bar */}
      <div className="mlist-toolbar">
        <form className="mlist-search" onSubmit={handleSearch}>
          <input
            className="mlist-search-input"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder={t('quality.search_product', 'Search by product code…')}
          />
          <button type="submit" className="btn-primary-sm">
            🔍 {t('search', 'Search')}
          </button>
          {search && (
            <button type="button" className="btn-ghost-sm" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>
              ✕ {t('clear', 'Clear')}
            </button>
          )}
        </form>
      </div>

      {/* Table */}
      {loading ? (
        <div className="mlist-loading">⏳ {t('loading', 'Loading…')}</div>
      ) : error ? (
        <div className="mlist-error">{error}</div>
      ) : (
        <>
          <div className="mlist-table-wrap">
            <table className="mlist-table">
              <thead>
                <tr>
                  <th>{t('quality.product_code', 'Product Code')}</th>
                  <th>{t('quality.version', 'Version')}</th>
                  <th>{t('quality.type', 'Type')}</th>
                  <th>{t('quality.phase', 'Phase')}</th>
                  <th className="text-center">{t('quality.lines', 'Lines')}</th>
                  <th className="text-center">{t('quality.sessions', 'Sessions')}</th>
                  <th>{t('quality.last_session', 'Last Session')}</th>
                  <th>{t('quality.status', 'Status')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {headers.length === 0 ? (
                  <tr><td colSpan={9} className="mlist-empty">{t('no_data', 'No specifications found')}</td></tr>
                ) : headers.map(h => (
                  <tr key={h.public_id} className="mlist-row"
                      onClick={() => navigate(`/quality/measurements/${h.public_id}`)}>
                    <td className="mlist-code">{h.product_code}</td>
                    <td>v{h.version_no}</td>
                    <td>{h.measurement_type}</td>
                    <td>{h.phase_code || '—'}</td>
                    <td className="text-center">{h.line_count ?? '—'}</td>
                    <td className="text-center">{h.session_count ?? 0}</td>
                    <td>{h.last_session_date ? new Date(h.last_session_date).toLocaleDateString('pt-PT') : '—'}</td>
                    <td>
                      <span className={`mlist-badge ${h.status === 1 ? 'badge-active' : 'badge-draft'}`}>
                        {h.status === 1 ? t('quality.active', 'Active') : t('quality.draft', 'Draft')}
                      </span>
                    </td>
                    <td>
                      <button className="btn-enter"
                        onClick={e => { e.stopPropagation(); navigate(`/quality/measurements/${h.public_id}`); }}>
                        {t('quality.enter', 'Enter Data')} →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mlist-pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="btn-page">‹ Prev</button>
              <span className="page-info">{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="btn-page">Next ›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MeasurementList;